import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from '@mui/material/utils';
import type { List } from 'immutable';

import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { getUiWorkspaceIsActiveContextMenuSelector } from '@/domain/ui';

import PaymentPreview from '@/pages/dokkaPay/pages/company/components/PaymentPreview';

import { getRotationAngle } from '@/pages/company/WorkspaceRoute/components/workSpace/helpers';

import { useApiToken } from '@/lib/apiTokenKeeper';
import {
  getPaymentPreviewSrc,
  getPaymentUrl,
} from '@/pages/dokkaPay/pages/company/components/PaymentsWorkspace/components/Workspace/components/Grid/helpers';

interface PaymentListItem {
  id: string;
  [key: string]: unknown;
}

interface PaymentPreviewNavigationProps {
  id: string | null;
  setPreview: (id: string | null) => void;
  list: List<PaymentListItem>;
  allCount: number;
  onContextMenu?: ((e: React.MouseEvent, id: string | null) => void) | null;
  onClick?: (id: string) => void;
  startIndex?: number;
  onNavigate: (direction: number) => void;
}

const PaymentPreviewNavigation: React.FC<PaymentPreviewNavigationProps> = ({
  id,
  setPreview,
  list,
  allCount,
  onContextMenu,
  onClick,
  startIndex = 0,
  onNavigate,
}) => {
  const apiToken = useApiToken();
  const companyId = useSelector(currentCompanyIdSelector);
  const isActiveContextMenu = useSelector(getUiWorkspaceIsActiveContextMenuSelector);
  const previousIndexInListRef = useRef(0);

  const indexInList = useMemo(() => {
    const indexInPayments = list.findIndex((item) => item.id === id);
    const adjustedIndex = indexInPayments < 0 ? previousIndexInListRef.current : indexInPayments;
    const index = Math.min(list.size - 1, adjustedIndex);

    previousIndexInListRef.current = index;

    return index;
  }, [id, list]);

  const { indexInAll, ordinalNumber, isShowLoader } = useMemo(() => {
    const indexInAllMemo = startIndex + indexInList;
    const ordinalNumberMemo = indexInAllMemo + 1;
    const isShowLoaderMemo = (ordinalNumberMemo === list.size && ordinalNumberMemo !== allCount) || indexInList === -1;

    return { indexInAll: indexInAllMemo, ordinalNumber: ordinalNumberMemo, isShowLoader: isShowLoaderMemo };
  }, [startIndex, indexInList, list.size, allCount]);

  const payment = useMemo(() => (indexInList >= 0 ? list.get(indexInList) : null), [indexInList, list]);

  const isOpenModal = payment && apiToken !== null;

  const src = useMemo(() => (id && companyId ? getPaymentPreviewSrc(companyId, id) : null), [companyId, id]);

  const documentLink = useMemo(() => (id && companyId ? getPaymentUrl(companyId, id) : ''), [companyId, id]);

  const onClose = useCallback(() => {
    setPreview(null);
  }, [setPreview]);

  const onPrev = useCallback(() => {
    const previousIndexInAll = indexInAll - 1;

    // we have not reached first payment in the entire list
    if (previousIndexInAll >= 0) {
      const previousIndexInList = indexInList - 1;
      const previousPayment = list.get(previousIndexInList);

      // for infinite loading we always have previous payment
      // because we are always starting from the beginning(startIndex === 0) and concat next chunk to this list
      if (previousPayment && previousIndexInList >= 0) {
        setPreview(previousPayment?.id);
      }

      // for paginated list we can start from the middle of list (startIndex === 500)
      // we have only current documents for the page in the list
      // we need to load the previous page of documents
      if (previousIndexInList < 0) {
        onNavigate(-1);
      }
    }
  }, [indexInAll, indexInList, list, setPreview, onNavigate]);

  const onPrevWithDelay = useMemo(() => debounce(onPrev, 200), [onPrev]);

  const onNext = useCallback(() => {
    if (indexInAll < allCount - 1) {
      const nextIndex = indexInList + 1;
      const nextPayment = list.get(nextIndex);

      if (nextPayment && nextIndex <= list.size - 1) {
        setPreview(nextPayment?.id);
      }

      if (nextIndex > list.size - 1) {
        onNavigate(1);
      }
    }
  }, [indexInAll, allCount, indexInList, list, setPreview, onNavigate]);

  const onNextWithDelay = useMemo(() => debounce(onNext, 200), [onNext]);

  const onKeyNav = useCallback(
    (e: KeyboardEvent) => {
      const actions: Record<string, () => void> = {
        ArrowLeft: onPrevWithDelay,
        ArrowRight: onNextWithDelay,
        Escape: onClose,
      };
      const action = actions[e.key];

      if (action) {
        action();
      }
    },
    [onPrevWithDelay, onNextWithDelay, onClose],
  );

  const onClickContextMenu = useCallback(
    (e: React.MouseEvent) => {
      onContextMenu?.(e, id);
    },
    [onContextMenu, id],
  );

  const onClickOpen = useCallback(() => {
    if (payment) onClick?.(payment.id);
  }, [onClick, payment]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyNav);
    return () => {
      window.removeEventListener('keydown', onKeyNav);
    };
  }, [onKeyNav]);

  return isOpenModal ? (
    <PaymentPreview
      src={src}
      to={documentLink}
      rotationAngle={getRotationAngle(payment)}
      onClick={onClickOpen}
      close={onClose}
      onPrev={onPrev}
      onNext={onNext}
      isDisablePrev={ordinalNumber === 1}
      isDisableNext={ordinalNumber === allCount}
      onContextMenu={typeof onContextMenu === 'function' && onClickContextMenu}
      isContextMenuOpen={isActiveContextMenu}
      allPagesCount={allCount}
      currentPage={ordinalNumber}
      isLoading={isShowLoader}
    />
  ) : null;
};

export default PaymentPreviewNavigation;
